import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { IoMdDownload } from "react-icons/io";
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import useDebounce from 'utils/customHooks/useDebounce';
// components
import Head from 'components/metatags_helmet/metatags';
import SectionLoader from 'components/loader/SectionLoader';
import PaginationLoader from 'components/loader/PaginationLoader';
import StatisticsDisplay from 'components/order-control/StatisticsDisplay';
import PieCharts from 'components/order-control/PieCharts';
import OrderControlFilters from 'components/order-control/OrderControlFilters';
import OrderControlTable from 'components/order-control/OrderControlTable';
import Pagination from 'components/userzone/Pagination';
//stores
import { useUserStore } from 'stores';
//api
import { getStatistics, getDetailedStatistics, getConcourseOrdersArticles } from 'api/concourse';
//helpers
import { exportExcel } from 'helpers';

const OrderControl = () => {
  const { t } = useTranslation(['backoffice', 'home', 'header']);
  const title = t('orderControl', { ns: 'backoffice' });
  const { isSpecialConcourseUser } = useUserStore();
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState(null);
  const [detailedStatistics, setDetailedStatistics] = useState(null);
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isExportingStatistics, setIsExportingStatistics] = useState(false);
  const [isExportingList, setIsExportingList] = useState(false);
  const loadingTimeoutRef = useRef(null);
  
  // Estados para los filtros
  const [filters, setFilters] = useState({
    ean: '',
    editorial: '',
    fechaPedido: null,
    titulo: '',
    biblioteca: '',
    autor: '',
    estado: ''
  });

  const debouncedEan = useDebounce(filters.ean, 800);
  const debouncedTitulo = useDebounce(filters.titulo, 800);
  const debouncedAutor = useDebounce(filters.autor, 800);

  const loadInitialStatistics = useCallback(async () => {
    try {
      setIsLoading(true);
      const [statisticsResponse, detailedResponse] = await Promise.all([
        getStatistics({}),
        getDetailedStatistics({})
      ]);
      
      if (statisticsResponse?.status === 200) {
        setStatistics(statisticsResponse.data);
      } else {
        console.error('Error al cargar estadísticas:', statisticsResponse);
      }
      
      if (detailedResponse?.status === 200) {
        setDetailedStatistics(detailedResponse.data);
      } else {
        console.error('Error al cargar estadísticas detalladas:', detailedResponse);
      }
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadOrders = useCallback(async (currentPage = 1, currentFilters = {}) => {
    try {
      setIsLoadingOrders(true);
      
      const articlesPayload = {
        page: currentPage,
        page_size: 10,
      };
      
      if (currentFilters.ean && currentFilters.ean.trim() !== '') {
        articlesPayload.ean = currentFilters.ean.trim();
      }
      if (currentFilters.titulo && currentFilters.titulo.trim() !== '') {
        articlesPayload.titulo = currentFilters.titulo.trim();
      }
      if (currentFilters.editorial && currentFilters.editorial.trim() !== '') {
        articlesPayload.editorial = currentFilters.editorial.trim();
      }
      if (currentFilters.biblioteca && currentFilters.biblioteca.trim() !== '') {
        articlesPayload.libreria = currentFilters.biblioteca.trim();
      }
      if (currentFilters.fechaPedido) {
        let fecha;
        if (currentFilters.fechaPedido instanceof Date) {
          // Usar moment para formatear la fecha sin conversión de zona horaria
          fecha = moment(currentFilters.fechaPedido).format('YYYY-MM-DD');
        } else if (typeof currentFilters.fechaPedido === 'string') {
          fecha = moment(currentFilters.fechaPedido).format('YYYY-MM-DD');
        } else {
          fecha = currentFilters.fechaPedido;
        }
        articlesPayload.fecha_pedido = fecha;
      }
      if (currentFilters.autor && currentFilters.autor.trim() !== '') {
        articlesPayload.autor = currentFilters.autor.trim();
      }
      if (currentFilters.estado && currentFilters.estado.trim() !== '') {
        articlesPayload.estado = currentFilters.estado.trim();
      }
      
      const articlesResponse = await getConcourseOrdersArticles(articlesPayload);
      
      if (articlesResponse?.status === 200 && articlesResponse?.data?.results) {
        setOrders(articlesResponse.data.results);
        const pages = articlesResponse.data.total_pages || Math.ceil((articlesResponse.data.count || 0) / 10) || 1;
        setTotalPages(pages);
      } else {
        console.error('Error al cargar artículos:', articlesResponse);
        setOrders([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error al cargar artículos:', error);
      setOrders([]);
      setTotalPages(1);
    } finally {
      setIsLoadingOrders(false);
    }
  }, []);

  const effectiveFilters = useMemo(() => ({
    ...filters,
    ean: debouncedEan,
    titulo: debouncedTitulo,
    autor: debouncedAutor
  }), [filters, debouncedEan, debouncedTitulo, debouncedAutor]);

  const prevFiltersRef = useRef(null);
  const prevPageRef = useRef(null);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (!isSpecialConcourseUser) {
      navigate('/');
      return;
    }
    
    loadInitialStatistics();
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      const initialFilters = {
        ean: '',
        editorial: '',
        fechaPedido: null,
        titulo: '',
        biblioteca: '',
        autor: '',
        estado: ''
      };
      loadOrders(1, initialFilters);
      prevFiltersRef.current = JSON.stringify(initialFilters);
      prevPageRef.current = 1;
    }
  }, [isSpecialConcourseUser, navigate, loadInitialStatistics, loadOrders]);

  useEffect(() => {
    if (!isSpecialConcourseUser || isFirstLoad.current) {
      return;
    }

    const currentFiltersStr = JSON.stringify(effectiveFilters);
    const filtersChanged = prevFiltersRef.current !== currentFiltersStr;
    const pageChanged = prevPageRef.current !== page;

    if (filtersChanged && !pageChanged) {
      if (page !== 1) {
        setPage(1);
        prevFiltersRef.current = currentFiltersStr;
        return;
      } else {
        prevFiltersRef.current = currentFiltersStr;
      }
    }

    if (filtersChanged || pageChanged) {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }

      loadingTimeoutRef.current = setTimeout(() => {
        loadOrders(page, effectiveFilters);
        prevFiltersRef.current = JSON.stringify(effectiveFilters);
        prevPageRef.current = page;
      }, 300);
    }

    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [isSpecialConcourseUser, page, effectiveFilters, loadOrders]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleExportStatistics = () => {
    const token = localStorage.getItem('access');
    setIsExportingStatistics(true);
    exportExcel(
      'concourse/concourse/export-statistics-excel/',
      {},
      token,
      'estadisticas_totales',
      () => setIsExportingStatistics(false)
    );
  };

  const handleExportList = () => {
    const token = localStorage.getItem('access');
    setIsExportingList(true);
    
    // Construir los parámetros de filtros igual que en loadOrders pero sin paginación
    const exportPayload = {};
    
    if (effectiveFilters.ean && effectiveFilters.ean.trim() !== '') {
      exportPayload.ean = effectiveFilters.ean.trim();
    }
    if (effectiveFilters.titulo && effectiveFilters.titulo.trim() !== '') {
      exportPayload.titulo = effectiveFilters.titulo.trim();
    }
    if (effectiveFilters.editorial && effectiveFilters.editorial.trim() !== '') {
      exportPayload.editorial = effectiveFilters.editorial.trim();
    }
    if (effectiveFilters.biblioteca && effectiveFilters.biblioteca.trim() !== '') {
      exportPayload.libreria = effectiveFilters.biblioteca.trim();
    }
    if (effectiveFilters.fechaPedido) {
      let fecha;
      if (effectiveFilters.fechaPedido instanceof Date) {
        fecha = moment(effectiveFilters.fechaPedido).format('YYYY-MM-DD');
      } else if (typeof effectiveFilters.fechaPedido === 'string') {
        fecha = moment(effectiveFilters.fechaPedido).format('YYYY-MM-DD');
      } else {
        fecha = effectiveFilters.fechaPedido;
      }
      exportPayload.fecha_pedido = fecha;
    }
    if (effectiveFilters.autor && effectiveFilters.autor.trim() !== '') {
      exportPayload.autor = effectiveFilters.autor.trim();
    }
    if (effectiveFilters.estado && effectiveFilters.estado.trim() !== '') {
      exportPayload.estado = effectiveFilters.estado.trim();
    }
    
    exportExcel(
      'storehouse/orders/articles/export-excel/',
      exportPayload,
      token,
      'lista_pedidos',
      () => setIsExportingList(false)
    );
  };

  if (!isSpecialConcourseUser) {
    return null;
  }

  return (
    <Container fluid className="margin-top-md mx-3 d-flex mainDiv order-control">
      <Head title={title} noIndex={true} />
      <Row className="w-100">
        <Col md={12} className="d-flex flex-row justify-content-between datetimepicker-userzone">
          <h1 className="page-title static-title d-flex justify-content-start">{t('orderControl', { ns: 'backoffice' })}</h1>
        </Col>
        <Col md={12}>
          {isLoading && <SectionLoader />}
          {!isLoading && (
            <>
              <StatisticsDisplay data={statistics} detailedData={detailedStatistics} />
              <PieCharts data={statistics} />
              <OrderControlFilters filters={filters} setFilters={setFilters} />
              {isLoadingOrders && (
                <div className="d-flex justify-content-center my-3">
                  <PaginationLoader visibility={isLoadingOrders} />
                </div>
              )}
              <OrderControlTable filters={filters} orders={orders} isLoading={isLoadingOrders} />
              {!isLoadingOrders && totalPages > 1 && (
                <Pagination 
                  page={page} 
                  totalPages={totalPages} 
                  changePage={handlePageChange} 
                />
              )}
              <div className="d-flex justify-content-end mt-3 mb-3 me-3">
                <Button 
                  variant="primary" 
                  className="me-2"
                  onClick={handleExportList}
                  disabled={isExportingList}
                >
                  <IoMdDownload className="me-2" />
                  {t('exportList', { ns: 'backoffice' })}
                </Button>
                <Button 
                  variant="primary"
                  onClick={handleExportStatistics}
                  disabled={isExportingStatistics}
                >
                  <IoMdDownload className="me-2" />
                  {t('exportTotals', { ns: 'backoffice' })}
                </Button>
              </div>
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default OrderControl;
